import Big from 'big.js'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useContext } from 'react'

import { formatDate, isTimeAfterNow, timeDiffFromToday } from '../utils'

import Button from './Button'
import CryptoCounter from './CryptoCounter'
import FiatCounter from './FiatCounter'
import IconContainer from './icons/IconContainer'
import { LockedMETContext } from './LockedMetContext'
import LockedPositionsEmptyState from './LockedPositionsEmptyState'

function LockedPositionsList() {
  const { t } = useTranslation()
  const { locale } = useRouter()

  const { openOperation, lockedPositions, claimables, totalClaimableInUsd } =
    useContext(LockedMETContext)

  const hasClaimables = claimables?.some(({ claimableAmount }) =>
    Big(claimableAmount).gt(0)
  )

  const formatUnlockTime = unlockTime =>
    formatDate(unlockTime * 1000, locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })

  return (
    <>
      {/* Claim Rewards Banner */}
      {hasClaimables && (
        <div className="from-teal-primary/20 to-teal-primary/10 border-teal-primary/30 mb-6 w-full rounded-lg border bg-gradient-to-r p-6 text-white">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-teal-primary/20 rounded-full p-3">
                <IconContainer height={48} name="met" width={48} />
              </div>
              <div className="flex-1">
                <div className="mb-1 text-sm text-gray-400">
                  {t('claimable-rewards')}
                </div>
                <div className="flex items-baseline gap-3">
                  <div className="text-3xl font-bold">
                    <CryptoCounter
                      end={claimables?.[0]?.claimableAmount || '0'}
                      suffix={` ${
                        claimables?.[0]?.rewardToken?.symbol || 'MET'
                      }`}
                    />
                  </div>
                  {totalClaimableInUsd && Big(totalClaimableInUsd).gt(0) && (
                    <div className="text-lg text-gray-400">
                      <FiatCounter end={totalClaimableInUsd} />
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Right: Claim button */}
            <div className="flex items-center md:min-w-[160px]">
              <Button
                className="w-full whitespace-nowrap"
                onClick={() => openOperation('claim')}
                size="lg"
              >
                {t('actions.claim')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {lockedPositions.length > 0 ? (
        <div className="md:bg-block w-full rounded-md bg-transparent text-white md:p-6 md:pt-8">
          <div className="mb-6 hidden justify-between px-4 md:flex">
            <h3 className="font-regular w-1/4 flex-none text-left text-sm">
              {t('locking-table.locked')}
            </h3>
            <h3 className="font-regular w-1/4 flex-none text-left text-sm">
              {t('locking-table.esmet')}
            </h3>
            <h3 className="font-regular w-1/4 flex-none text-left text-sm">
              {t('locking-table.expire-date')}
            </h3>
            <h3 className="font-regular w-1/4 flex-none text-left text-sm">
              {t('locking-table.actions')}
            </h3>
          </div>
          <div className="md:bg-blue-secondary space-y-4 rounded-lg md:py-2">
            {lockedPositions.map(
              ({
                boostedAmount,
                lockedAmount,
                lockedAmountInUsd,
                tokenId,
                unlockTime
              }) => (
                <div
                  className="
                    bg-blue-secondary
                    rounded-lg
                    p-4
                    pb-7
                    md:mx-2
                    md:flex
                    md:h-16
                    md:items-center
                    md:bg-transparent
                    md:p-0
                    md:px-2
                    md:hover:rounded-lg
                  "
                  key={tokenId}
                >
                  <div className="mb-2 flex justify-between pb-2 md:mb-0 md:w-1/4 md:pb-0">
                    <span className="text-gray-700 md:hidden">
                      {t('locking-table.locked')}
                    </span>
                    <div className="relative min-h-[40px]">
                      <IconContainer
                        className="absolute top-0"
                        height={40}
                        name="met"
                        width={40}
                      />
                      <div className="ml-14">
                        <CryptoCounter end={lockedAmount} suffix=" MET" />
                      </div>
                      <div className="ml-14 text-sm">
                        <FiatCounter end={lockedAmountInUsd} />
                      </div>
                    </div>
                  </div>
                  <div className="mb-2 flex justify-between pb-2 md:mb-0 md:w-1/4 md:pb-0">
                    <span className="text-gray-700 md:hidden">
                      {t('locking-table.esmet')}
                    </span>
                    <div className="relative min-h-[40px]">
                      <IconContainer
                        className="absolute top-0"
                        height={40}
                        name="esmet"
                        width={40}
                      />
                      <div className="ml-14 flex h-full items-center">
                        <CryptoCounter
                          end={Big(lockedAmount).plus(boostedAmount)}
                          suffix=" esMET"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mb-2 flex justify-between pb-2 md:mb-0 md:w-1/4 md:pb-0">
                    <span className="text-gray-700 md:hidden">
                      {t('locking-table.expire-date')}
                    </span>

                    <div className="relative">
                      <div>{formatUnlockTime(unlockTime)}</div>
                      <div className="text-gray-primary text-xs">
                        {isTimeAfterNow(unlockTime)
                          ? timeDiffFromToday(unlockTime, locale)
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div
                    className="
                    mt-5
                    flex
                    justify-between
                    space-x-2
                    whitespace-nowrap
                    md:m-0
                    md:flex
                    md:w-1/4
                    md:justify-normal
                    md:space-x-3
                    md:py-2
                    "
                  >
                    <Button
                      className="xl:width-inherit mb-1 inline-block w-full whitespace-nowrap py-2 xl:mb-0 xl:px-7"
                      onClick={() => openOperation('unlock', { tokenId })}
                      size="lg"
                    >
                      {t('actions.unlock')}
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      ) : (
        <LockedPositionsEmptyState />
      )}
    </>
  )
}

export default LockedPositionsList
